import * as React from 'react';
import { useMemo, useRef, forwardRef } from 'react';
import { GridRowModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { CrudDataGrid, CrudConfig, CrudDataGridHandle, CrudField } from '../CrudDataGrid';
import {
  AutocompleteEditCell,
  getOptionLabelFromValue,
  type AutocompleteOption,
} from '../common/AutocompleteEditCell';
import { YearDatePicker } from '../common/YearDatePicker';
import { DECIMAL_PLACES } from '../../constants/decimalPlacesPolicy';
import { DeleteStatusResponse } from '../../generated/api-client';

const powerSourceOptions: AutocompleteOption[] = [
  { value: 'GN', label: 'Gas naturale' },
  { value: 'H2', label: 'Idrogeno' },
  { value: 'BIO', label: 'Biomasse' },
  { value: 'SOL', label: 'Energia solare' },
  { value: 'IDR', label: 'Energia idroelettrica' },
  { value: 'EOL', label: 'Energia eolica' },
  { value: 'ALT', label: 'Altro (specificare)' },
];

const typeOptions: AutocompleteOption[] = [
  { value: 'COG', label: 'Cogenerazione' },
  { value: 'FV', label: 'Fotovoltaico' },
  { value: 'IDR', label: 'Idroelettrico' },
  { value: 'EOL', label: 'Eolico' },
  { value: 'ORC', label: 'ORC' },
];

function getTypeLabel(value: string | null | undefined): string {
  return getOptionLabelFromValue(typeOptions, value);
}

const fields: CrudField[] = [
  {
    field: 'type',
    headerName: 'Tipologia impianto',
    width: 200,
    type: 'string',
    renderEditCell: params => (
      <AutocompleteEditCell
        {...params}
        options={typeOptions}
        placeholder="Tipologia impianto"
      />
    ),
    valueFormatter: v => getTypeLabel(v as string),
  },
  {
    field: 'power_source',
    headerName: 'Alimentazione',
    width: 220,
    type: 'string',
    renderEditCell: params => (
      <AutocompleteEditCell
        {...params}
        options={powerSourceOptions}
        placeholder="Alimentazione"
      />
    ),
    valueFormatter: v => getOptionLabelFromValue(powerSourceOptions, v as string),
  },
  {
    field: 'in_service_year',
    headerName: 'Anno entrata in esercizio',
    width: 160,
    type: 'date',
    // Solo anno: editor dedicato (YearDatePicker) e visualizzazione "AAAA" invece della data
    // completa. Il valore resta una Date (1° gennaio dell'anno), nessun impatto su schema/API.
    renderEditCell: params => <YearDatePicker {...params} />,
    valueFormatter: v => (v ? dayjs(v as Date).format('YYYY') : ''),
  },
  {
    field: 'electrical_power_kw',
    headerName: 'Potenza elettrica (kW)',
    width: 200,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    decimalPlaces: DECIMAL_PLACES.potenza_elettrica_termica_frigorifera,
  },
  {
    field: 'thermal_power_kw',
    headerName: 'Potenza termica (kW)',
    width: 200,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    decimalPlaces: DECIMAL_PLACES.potenza_elettrica_termica_frigorifera,
  },
  {
    field: 'cooling_power_kw',
    headerName: 'Potenza frigorifera (kW)',
    width: 200,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    decimalPlaces: DECIMAL_PLACES.potenza_elettrica_termica_frigorifera,
  },
];

export interface SelfProductionPlantsGridProps<TRead, TCreate> {
  queryKey: string;
  listFn: () => Promise<TRead[]>;
  createFn: (data: TCreate) => Promise<TRead>;
  updateFn: (id: string, data: TCreate) => Promise<TRead>;
  deleteFn: (id: string) => Promise<void | DeleteStatusResponse>;
  getId: (row: TRead) => string;
  getCreateDto: (row: GridRowModel) => TCreate;
  height?: string | number;
}

export const SelfProductionPlantsGrid = forwardRef<
  CrudDataGridHandle,
  SelfProductionPlantsGridProps<any, any>
>(({ queryKey, listFn, createFn, updateFn, deleteFn, getId, getCreateDto, height }, ref) => {
  const internalRef = useRef<CrudDataGridHandle>(null);

  const config = useMemo<CrudConfig<any, any>>(
    () => ({
      queryKey,
      fields,
      listFn,
      createFn,
      updateFn,
      deleteFn,
      getId,
      getCreateDto,
    }),
    [queryKey, listFn, createFn, updateFn, deleteFn, getId, getCreateDto]
  );

  React.useImperativeHandle(ref, () => ({
    addRow: () => internalRef.current?.addRow(),
  }));

  return <CrudDataGrid ref={internalRef} config={config} height={height} />;
});

SelfProductionPlantsGrid.displayName = 'SelfProductionPlantsGrid';

export const selfProductionPowerSourceOptions = powerSourceOptions;
export const selfProductionTypeOptions = typeOptions;
